import { buildPhotoUrl } from '@shared/utils/photoUrlDialect';
import type { ApplicationContext, SpriteImageSource } from './styleApplicator.types';
import type { CSSObject, Sprite } from './themeOutput.types';

/**
 * Theme art as `<img>` (PRNT-4898 follow-up).
 *
 * The published sprite is a CSS `background-image`, which the browser only discovers after CSSOM
 * and layout — never from the preload scanner. An `<img>` in the SSR markup is discovered while
 * the HTML streams, and can be told to load eagerly rather than on approach.
 * These helpers derive the `<img>` attributes and the matching art-layer style from
 * the sprite JSON; the server-side preload links are built from the same functions so a
 * preload always resolves to the URL the image itself will pick (a mismatch downloads twice).
 *
 * Deliberately NO fetchpriority either, on either consumer. The header photo is the LCP element
 * and keeps the high band to itself; theme art is decoration that merely needs to start early,
 * which `<img>` discovery and the `<head>` preload already give it. Marking art high put a
 * 5120x2880 sprite in front of the hero on the worst themes. Both sides must stay silent on
 * priority together — one side alone leaves the browser free to promote the request anyway.
 *
 * Candidates come from `resize=w`, NOT from the `@Nx` siblings. `@Nx` names a pre-rendered file,
 * so a rendition ladder is expressible — but those siblings have never been requested, so each is
 * cold at every edge: measured 0.9-1.2s to generate, and mid-generation the origin can answer with
 * a body Chrome's Opaque Response Blocking rejects outright (ERR_BLOCKED_BY_ORB, naturalWidth 0 —
 * the art never paints). `resize=w` stays on the one published file and is the dialect the photo
 * stack already uses for this backend, so it is the safer axis.
 *
 * Why this is worth doing: the published art is sized for nothing in particular. Across the
 * bundle, `original` sprites land at a median 1.99x their CSS box (right for DPR 2), while
 * viewport-relative sprites measure 0.87x at 1440/DPR1 and 0.44x at DPR 2 — under-sized — yet a
 * tail reaches 8288px and 5.3MB. One fixed size for every device means phones download desktop art
 * while retina desktops get soft art. A `sizes` + `srcSet` pair lets the browser pick per device.
 */

const IMAGE_LAYER = '& > *:first-child';
const BACKGROUND_PROPERTIES = new Set([
  'backgroundImage',
  'backgroundSize',
  'backgroundRepeat',
  'backgroundPosition',
  'backgroundPositionX',
  'backgroundPositionY',
]) as ReadonlySet<string>;

/**
 * Mirrors HIGH_RES_SRCSET_WIDTHS_PX in the photo stack
 * (apps/guest/.../OptimizedEventGuestSitePhoto/renditions.ts) — restated rather than imported
 * because `@shared` must not depend on `@apps`. Keep the two in step.
 *
 * Deliberately short: every distinct width is its own cache entry in a transcode-on-read service,
 * and each new one is cold on first request. 2048 is also the ceiling that caps a DPR-3 phone,
 * which is the MAX_EFFECTIVE_DPR intent expressed through the ladder instead of by deflating
 * `sizes`.
 */
const SRCSET_WIDTHS_PX: ReadonlyArray<number> = [640, 960, 1280, 1600, 2048];

/**
 * For a URL with no `resize=`, `asset.width` names the `@3x` master and the `@2x` file the JSON
 * points at is two thirds of it (amethyst_tidepool: width 1761, the CDN serves 1174px). It is NOT
 * a universal rule: about half the bundled sprites carry `resize=wN` with N equal to `asset.width`,
 * where the width is the author's design width and the file is far larger (abstract_boho: width
 * 1920, `resize=w1920`, and w4000 returns 3413px). Applied alone it capped those at w1280 — soft
 * on every retina desktop that used to get the published w1920. `provenFileWidth` combines it
 * with the resize the URL already proves.
 */
const AT_2X_OF_MASTER = 2 / 3;

/**
 * A lower bound on the pixel width the published file can deliver, or 0 when nothing is known.
 * Two independent facts, and the larger wins: the `@2x`-of-master estimate from `asset.width`,
 * and a `resize=wN` already on the URL — the origin serves N only when the file has N to give,
 * and the published URL has been serving it, so N is proven. Over-asking beyond either costs one
 * cache entry (the origin caps at natural size), under-asking costs quality on every large screen.
 */
const provenFileWidth = (url: string, assetWidth: number | undefined): number => {
  const fromMaster = Math.round((assetWidth || 0) * AT_2X_OF_MASTER);
  const resize = /[?&]resize=w(\d+)(?:&|$)/.exec(url);
  const fromUrl = resize ? Number(resize[1]) : 0;
  return Math.max(fromMaster, fromUrl);
};

/**
 * The sprite's own CSS box as a `sizes` value, or undefined when it cannot be stated honestly.
 *
 * Over-estimates only, per the rule in the photo stack's ssrHeroSizes: an over-estimate costs at
 * most one wasted srcset bucket, an under-estimate re-fires LCP with a second download. A
 * percentage box is a fraction of the FRAME, and the frame is never wider than the viewport, so
 * reading it as `vw` rounds up.
 */
const toSizes = (style: CSSObject): string | undefined => {
  const { width } = style;
  if (typeof width === 'number' && width > 0) {
    return `${Math.ceil(width)}px`;
  }
  if (typeof width === 'string') {
    const percent = /^(\d+(?:\.\d+)?)%$/.exec(width.trim());
    if (percent) {
      return `${Math.ceil(Number(percent[1]))}vw`;
    }
  }
  return undefined;
};

/**
 * The single source of truth for a sprite's image candidates, shared by the `<img>` and the
 * server's `<link rel="preload">` so the two can never disagree — a preload that resolves to a
 * different URL than the image picks downloads the art twice.
 *
 * Falls back to the published URL alone whenever a ladder would be a lie: a box whose width is not
 * expressible, a URL on neither image backend (buildPhotoUrl returns it untouched, so every
 * candidate would be identical), or an asset too small for more than one rung.
 */
export const buildSpriteImageSource = (sprite: Sprite): SpriteImageSource => {
  const src = sprite.asset.url;
  const sizes = toSizes(sprite.computedStyleObject);
  if (!sizes) {
    return { src };
  }

  // Never offer a candidate the file cannot satisfy: the origin would silently serve the natural
  // size, burning a cache entry to return bytes an existing rung already covers. An unknown
  // width is the least trusted input, so it gets the most conservative answer — the published
  // URL alone — rather than the full ladder.
  const natural = provenFileWidth(src, sprite.asset.width);
  const widths = SRCSET_WIDTHS_PX.filter(width => width <= natural);
  if (widths.length < 2) {
    return { src };
  }

  const candidates = widths.map(width => ({ width, url: buildPhotoUrl(src, { width }) }));
  // buildPhotoUrl passes through anything outside /media/ and /assets/public/ unchanged.
  if (candidates.every(candidate => candidate.url === src)) {
    return { src };
  }

  return { src, srcSet: candidates.map(({ width, url }) => `${url} ${width}w`).join(', '), sizes };
};

/**
 * The candidate a viewport needing `requiredPx` image pixels would select, mirroring the browser:
 * the narrowest rung that covers it, or the widest rung when none does. Undefined without a
 * ladder. The SSR preload uses it for `href`: browsers without `imagesrcset` support (Safari and
 * iOS before 17.2) preload `href` and ignore the ladder, so pointing `href` at the published file
 * would fetch the master from `<head>` and then a rung for the `<img>` — the double download the
 * preload exists to prevent, on the browsers with the least headroom. One `href` cannot match
 * every viewport in a media bucket, so the caller states the viewport that cohort actually has.
 */
export const candidateForViewport = (source: SpriteImageSource, requiredPx: number): string | undefined => {
  if (!source.srcSet) {
    return undefined;
  }
  const candidates = source.srcSet
    .split(', ')
    .map(candidate => /^(\S+) (\d+)w$/.exec(candidate))
    .flatMap(match => (match ? [{ width: Number(match[2]), url: match[1] }] : []))
    .sort((a, b) => a.width - b.width);
  const covering = candidates.find(candidate => candidate.width >= requiredPx);
  return (covering ?? candidates[candidates.length - 1])?.url;
};

/**
 * Rewrites the sprite's art-layer block for an `<img>`: every published sprite is `no-repeat`
 * with `contain` or `cover` and a keyword position, which is exactly `object-fit` +
 * `object-position`. Input is never mutated — the ThemeOutput it comes from is shared through the
 * fetch cache.
 */
export const toImageLayerStyle = (style: CSSObject): CSSObject => {
  const layer = style[IMAGE_LAYER];
  if (!layer || typeof layer !== 'object') {
    return style;
  }

  const { backgroundSize, backgroundPositionX, backgroundPositionY } = layer as CSSObject;
  const rewritten: CSSObject = {
    objectFit: backgroundSize === 'cover' ? 'cover' : 'contain',
    objectPosition: `${backgroundPositionX ?? 'center'} ${backgroundPositionY ?? 'center'}`,
  };
  Object.entries(layer).forEach(([property, value]) => {
    if (!BACKGROUND_PROPERTIES.has(property)) {
      rewritten[property] = value;
    }
  });

  return { ...style, [IMAGE_LAYER]: rewritten };
};

/**
 * Whether a sprite paints in the first viewport, decided from the JSON alone so the server
 * (which art to preload) and the client (`loading` / `decoding`) always agree. Viewport-fixed art
 * is always on screen; the title banner is the page top; top-anchored frame art and
 * frame-filling centred art start at the top of a frame that begins at the top of the page.
 * Only bottom-anchored (and non-filling centred) art is left to lazy-load.
 */
export const isAboveTheFold = (sprite: Sprite, context: ApplicationContext): boolean => {
  if (sprite.fixedPositionWhenScrolling || 'component' in context) {
    return true;
  }
  if (sprite.placement.y === 'top') {
    return true;
  }
  return sprite.placement.y === 'center' && sprite.scale.constraint === 'parent-fill';
};
